import { memo, useState } from "react";
import { TextField } from "@mui/material";
import { Dropdown } from "semantic-ui-react";
import { CreateHyperlink } from "./UriEntry";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";

const CodingDisplay = ({ coding }) => {
    if (coding) {
        if (Array.isArray(coding) || typeof coding !== "object") {
            return <>[ERROR: Coding data not interpretable.]</>;
        }
        let code = coding.code || "[Code missing.]";
        let system = coding.system || "[Code System missing.]";
        let systemDisplay;
        if (system === "http://snomed.info/sct") {
            systemDisplay = "SNOMED-CT";
        } else if (system === "http://loinc.org") {
            systemDisplay = "LOINC";
        } else if (system === "http://www.nlm.nih.gov/research/umls/rxnorm") {
            systemDisplay = "RxNorm";
        } else if (system === "http://unitsofmeasure.org") {
            systemDisplay = "UCUM";
        }
        if (coding.display && systemDisplay) {
            return <>{coding.display} (<CreateHyperlink url={system} display={systemDisplay || system} openInNewTab={true} /> code: {code})</>
        } else if (coding.display && !systemDisplay) {
            return <>{coding.display} (coded as: {code} from <CreateHyperlink url={system} display={systemDisplay || system} openInNewTab={true} />)</>
        } else if (systemDisplay) {
            return <><CreateHyperlink url={system} display={systemDisplay || system} openInNewTab={true} /> code: {code}</>
        } else {
            return <>code {code} from <CreateHyperlink url={system} display={systemDisplay || system} openInNewTab={true} /></>
        }
    } else {
        return <></>
    }
}

const displayStringFromFHIRCoding = (coding) => {
    if (coding === undefined || coding === null || coding === '') {
        return ""
    }
    if (typeof coding === "object") {
        try {
            let codingDisplay = "";
            let code = coding.code || "[Code missing.]";
            let system = coding.system || "[Code System missing.]";
            let systemDisplay;
            if (system === "http://snomed.info/sct") {
                systemDisplay = "SNOMED-CT";
            } else if (system === "http://loinc.org") {
                systemDisplay = "LOINC";
            } else if (system === "http://www.nlm.nih.gov/research/umls/rxnorm") {
                systemDisplay = "RxNorm";
            } else if (system === "http://unitsofmeasure.org") {
                systemDisplay = "UCUM";
            }
            if (coding.display && systemDisplay) {
                codingDisplay = coding.display + " (" + systemDisplay + " code: " + code + ")";
            } else if (coding.display && !systemDisplay) {
                codingDisplay = coding.display + " (coded as: " + code + " from " + system + ")";
            } else if (systemDisplay) {
                codingDisplay = systemDisplay + " code: " + code;
            } else {
                codingDisplay = "code " + code + " from " + system;
            }
            if (codingDisplay && typeof codingDisplay === "string") {
                return codingDisplay;
            }
        } catch (e) { console.log(e); }
    }
    if (typeof coding === "string") {
        return `${coding} [ERROR: Coding data not in expected Coding datatype.]`
    }
    return "[ERROR: Coding data not interpretable.]";
}

const CodingEntryWithSystemSelector = memo(({ elementName, fieldLabel, startingValue,
  setResourceState, systemChoices, systemChoicesOpen, inTableCell,
  startCollapsed }) => {
  if (!startingValue) {
    startingValue = "";
  }
  let startingSystemChoices = JSON.parse(JSON.stringify(systemChoices));
  if (!startingSystemChoices) {
    startingSystemChoices = [
      { "uri": "https://fevir.net", "display": "FEvIR" },
      { "uri": "http://snomed.info/sct", "display": "SNOMED-CT" }
    ];
  }
  let currentSystemValue = startingValue?.system || "";
  if (currentSystemValue) {
    let matchFound = false;
    for (const choice of startingSystemChoices) {
      if (currentSystemValue === choice.uri) {
        matchFound = true;
      }
    }
    if (!matchFound) {
      startingSystemChoices.push({ "uri": currentSystemValue, "display": currentSystemValue });
    }
  }

  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRCoding(startingValue)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "18px" }}>
        {!inTableCell && <b>System: </b>}
        <Dropdown
          name={"picklist-for-" + elementName}
          placeholder="Select System or Enter URL"
          search allowAdditions additionLabel={"Enter System URL: "}
          closeOnChange selection clearable selectOnBlur={false} autoComplete="off"
          style={{ width: "35%" }}
          options={startingSystemChoices.map((choice, choiceIndex) => { return { "key": choiceIndex, "value": choice.uri, "text": choice.display } })}
          value={currentSystemValue}
          onAddItem={(e, data) => {
            startingSystemChoices.push({ "uri": data.value, "display": data.value });
          }}
          onChange={(e, data) => {
            let selectedSystem;
            for (const choice of startingSystemChoices) {
              if (choice.uri === data.value) {
                selectedSystem = choice.uri;
              }
            }
            if (systemChoicesOpen && !selectedSystem) {
              selectedSystem = data.value;
            }
            let newCoding = { system: "", code: "", display: "" };
            newCoding.system = selectedSystem;
            if (startingValue.code) { newCoding.code = startingValue.code; } else { delete newCoding.code; }
            if (startingValue.display) { newCoding.display = startingValue.display; } else { delete newCoding.display; }
            if (startingValue.code || startingValue.display || selectedSystem) {
              setResourceState(prevState => { return { ...prevState, [elementName]: newCoding } });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} } });
            }
          }}
        />
        {!inTableCell && <br />}
        {inTableCell && <><br /><div style={{ paddingTop: "8px" }} /></>}
        <TextField style={{ width: inTableCell ? "90%" : "25%" }}
          className="inputField" type='text' label={'Code'}
          size="small" variant='outlined' value={startingValue.code || ''}
          onChange={(e) => {
            let newCoding = { system: "", version: "", code: "", display: "" };
            newCoding.code = e.target.value;
            if (startingValue.version) { newCoding.version = startingValue.version; } else { delete newCoding.version; }
            if (startingValue.system) { newCoding.system = startingValue.system; } else { delete newCoding.system; }
            if (startingValue.display) { newCoding.display = startingValue.display; } else { delete newCoding.display; }
            if (startingValue.system || startingValue.display || e.target.value || startingValue.version) {
              setResourceState(prevState => { return { ...prevState, [elementName]: newCoding } });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} } });
            }
          }} />
        {inTableCell && <><br /><div style={{ paddingTop: "8px" }} /></>}
        <TextField style={{ width: inTableCell ? "90%" : "35%" }}
          className="inputField" type='text' label={'Display'}
          size="small" variant='outlined' value={startingValue.display || ''}
          onChange={(e) => {
            let newCoding = { system: "", version: "", code: "", display: "" };
            newCoding.display = e.target.value;
            if (startingValue.version) { newCoding.version = startingValue.version; } else { delete newCoding.version; }
            if (startingValue.system) { newCoding.system = startingValue.system; } else { delete newCoding.system; }
            if (startingValue.code) { newCoding.code = startingValue.code; } else { delete newCoding.code; }
            if (startingValue.code || startingValue.system || e.target.value || startingValue.version) {
              setResourceState(prevState => { return { ...prevState, [elementName]: newCoding } });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} } });
            }
          }} />
      </div>
    </>
  }
});

const CodingEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState, valueSet,
    inTableCell, startCollapsed }) => {
  
    if (!startingValue) {
      startingValue = "";
    }
  
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);
  
    if (startCollapsedState) {
      return <>
        <p>
          <b>{fieldLabel}: </b>
          {startingValue && displayStringFromFHIRCoding(startingValue)}
          &nbsp;&nbsp;
          <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </p>
      </>
    } else {
      return <>
        <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
        {valueSet && <Dropdown
          name={"picklist-for-" + elementName}
          placeholder="Preset" search
          closeOnChange selection clearable selectOnBlur={false} autoComplete="off"
          style={{ minWidth: "25%", width: "25%" }}
          options={valueSet.map((term, termIndex) => { return { "key": termIndex, "value": term.code, "text": term.display } })}
          value={startingValue.code}
          onChange={(e, data) => {
            let selectedCode;
            let selectedSystem;
            let selectedDisplay;
            let selectedVersion;
            for (const term of valueSet) {
              if (term.code === data.value) {
                selectedCode = term.code;
                selectedSystem = term.system || "";
                selectedDisplay = term.display || "";
                selectedVersion = term.version || "";
              }
            }
            let newCoding = { system: selectedSystem, version: selectedVersion, code: selectedCode, display: selectedDisplay };
            if (newCoding.version === "") {
              delete newCoding.version;
            }
            if (selectedCode || selectedDisplay || selectedSystem || selectedVersion) {
                setResourceState(prevState => { return { ...prevState, [elementName]: newCoding } });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} } });
            }
          }}
        />
        }
        {!inTableCell && <>&nbsp;&nbsp;&nbsp;</>}
        <TextField style={{ width: inTableCell ? "90%" : "25%" }}
          className="inputField" type='text' label={'System'}
          size="small" variant='outlined' value={startingValue.system || ''}
          onChange={(e) => {
            let newCoding = { system: "", version: "", code: "", display: "" };
            newCoding.system = e.target.value;
            if (startingValue.version) { newCoding.version = startingValue.version; } else { delete newCoding.version; }
            if (startingValue.code) { newCoding.code = startingValue.code; } else { delete newCoding.code; }
            if (startingValue.display) { newCoding.display = startingValue.display; } else { delete newCoding.display; }
            if (startingValue.code || startingValue.display || e.target.value || startingValue.version) {
                setResourceState(prevState => { return { ...prevState, [elementName]: newCoding } });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} } });
            }
          }} />
        {inTableCell ? <><br /><div style={{ marginTop: "8px" }} /></> : <>&nbsp;&nbsp;&nbsp;</>}
        <TextField style={{ width: inTableCell ? "90%" : "25%" }}
          className="inputField" type='text' label={'Code'}
          size="small" variant='outlined' value={startingValue.code || ''}
          onChange={(e) => {
            let newCoding = { system: "", version: "", code: "", display: "" };
            newCoding.code = e.target.value;
            if (startingValue.version) { newCoding.version = startingValue.version; } else { delete newCoding.version; }
            if (startingValue.system) { newCoding.system = startingValue.system; } else { delete newCoding.system; }
            if (startingValue.display) { newCoding.display = startingValue.display; } else { delete newCoding.display; }
            if (startingValue.system || startingValue.display || e.target.value || startingValue.version) {
                setResourceState(prevState => { return { ...prevState, [elementName]: newCoding } });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} } });
            }
          }} />
        {inTableCell ? <><br /><div style={{ paddingTop: "8px" }} /></> : <>&nbsp;&nbsp;&nbsp;</>}
        <TextField style={{ width: inTableCell ? "90%" : "25%" }}
          className="inputField" type='text' label={'Display'}
          size="small" variant='outlined' value={startingValue.display || ''}
          onChange={(e) => {
            let newCoding = { system: "", version: "", code: "", display: "" };
            newCoding.display = e.target.value;
            if (startingValue.version) { newCoding.version = startingValue.version; } else { delete newCoding.version; }
            if (startingValue.system) { newCoding.system = startingValue.system; } else { delete newCoding.system; }
            if (startingValue.code) { newCoding.code = startingValue.code; } else { delete newCoding.code; }
            if (startingValue.code || startingValue.system || e.target.value || startingValue.version) {
              setResourceState(prevState => { return { ...prevState, [elementName]: newCoding } });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} } });
            }
          }} />
      </>
    }
  });

export default CodingEntry;
export { CodingDisplay, CodingEntryWithSystemSelector, displayStringFromFHIRCoding };